import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { presentMutationError, toastError } from "../utils/showApiErrorToast";
import { notifyTradesUpdated } from "../hooks/useTradeStatistics";
import { useActiveBrokerAccount } from "../hooks/useActiveBrokerAccount";
import {
  scanInstrumentSetup,
} from "../services/brokerIntegrationApi";
import { setMarketDataPreferencesBatch, skipMarketDataPreference, InstrumentsApiError } from "../services/instrumentsApi";
import type {
  InstrumentSetupResponse,
  InstrumentSetupRow,
  MarketDataPreferenceSkipReason,
} from "../types/instrumentSetup";
import {
  buildInstrumentSetupBatchItem,
  describeInstrumentSetupSaveBlockers,
  type InstrumentRowEditorSelection,
  isInstrumentSetupRowReadyToSave,
} from "../utils/instrumentSetupSave";
import {
  loadInstrumentSetupForDisplay,
  refreshInstrumentSetupAfterMutation,
} from "../utils/instrumentSetupLoad";
import {
  accountNeedsInstrumentSetup,
  isInstrumentSetupPending,
  shouldBlockInstrumentSetupForCharts,
  sortInstrumentSetupPending,
} from "../utils/instrumentSetupGate";
import { InstrumentSetupWizard } from "../components/Instruments/InstrumentSetupWizard";

export type OpenInstrumentSetupOptions = {
  focusSymbol?: string;
  mandatory?: boolean;
  /** Keep wizard open when setup is complete (e.g. Layer B OHLC reconnect / switch source). */
  forceOpen?: boolean;
  /** After sync when BE signalled setup — keep modal open if first load is empty. */
  expectedPending?: boolean;
  /** Reuse scan result from post-sync to avoid a second round-trip. */
  initialSetupData?: InstrumentSetupResponse | null;
};

type InstrumentSetupContextValue = {
  isOpen: boolean;
  mandatory: boolean;
  brokerAccountCode: string | null;
  focusSymbol: string | null;
  setupData: InstrumentSetupResponse | null;
  loading: boolean;
  saving: boolean;
  openSetup: (accountCode: string, options?: OpenInstrumentSetupOptions) => Promise<void>;
  closeIfComplete: () => void;
  refreshScan: () => Promise<InstrumentSetupResponse | null>;
  dismissWizard: () => void;
  saveRow: (
    row: InstrumentSetupRow,
    selection: InstrumentRowEditorSelection,
    consentGranted: boolean,
    resolveContext?: { md_listing_key?: string; instrument_id?: string; asset_class?: string }
  ) => Promise<boolean>;
  skipRow: (
    row: InstrumentSetupRow,
    reason?: MarketDataPreferenceSkipReason
  ) => Promise<boolean>;
  isBlockingActiveAccount: boolean;
  activeAccountPending: boolean | null;
  hydratePendingForActiveAccount: (options?: { symbol?: string }) => Promise<boolean>;
};

const InstrumentSetupContext = createContext<InstrumentSetupContextValue | null>(null);

export function InstrumentSetupProvider({ children }: { children: React.ReactNode }) {
  const { accounts, activeAccountCode, refetch } = useActiveBrokerAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [brokerAccountCode, setBrokerAccountCode] = useState<string | null>(null);
  const [focusSymbol, setFocusSymbol] = useState<string | null>(null);
  const [setupData, setSetupData] = useState<InstrumentSetupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const forceOpenSessionRef = useRef(false);
  const setupDataRef = useRef(setupData);
  setupDataRef.current = setupData;

  const activeAccount = useMemo(
    () => accounts.find((a) => a.accountCode === activeAccountCode) ?? null,
    [accounts, activeAccountCode]
  );

  const activeAccountPending = activeAccount?.instrumentSetupPending ?? null;
  const activeAccountPendingRef = useRef(activeAccountPending);
  activeAccountPendingRef.current = activeAccountPending;

  const isBlockingActiveAccount =
    setupData != null
      ? accountNeedsInstrumentSetup(setupData)
      : isInstrumentSetupPending(activeAccountPending);

  const closeIfComplete = useCallback(() => {
    if (forceOpenSessionRef.current) return;
    setSetupData((prev) => {
      if (prev && !accountNeedsInstrumentSetup(prev)) {
        setIsOpen(false);
        setMandatory(false);
        setFocusSymbol(null);
      }
      return prev;
    });
  }, []);

  const dismissWizard = useCallback(() => {
    forceOpenSessionRef.current = false;
    setIsOpen(false);
    setMandatory(false);
    setFocusSymbol(null);

    const code = brokerAccountCode?.trim();
    if (!code) return;

    void (async () => {
      try {
        const data = await refreshInstrumentSetupAfterMutation(code);
        setSetupData(data);
        await refetch();
      } catch {
        // Best-effort — wizard gate hooks re-load on close as well.
      }
    })();
  }, [brokerAccountCode, refetch]);

  const refreshScan = useCallback(async (): Promise<InstrumentSetupResponse | null> => {
    const code = brokerAccountCode?.trim();
    if (!code) return null;
    setLoading(true);
    try {
      const data = await scanInstrumentSetup(code);
      setSetupData(data);
      await refetch();
      if (!accountNeedsInstrumentSetup(data) && !forceOpenSessionRef.current) {
        setIsOpen(false);
        setMandatory(false);
        setFocusSymbol(null);
        toast.success("Instrument setup complete");
      }
      return data;
    } catch (e) {
      presentMutationError(e, "Failed to refresh instrument setup.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [brokerAccountCode, refetch]);

  const openSetup = useCallback(
    async (accountCode: string, options?: OpenInstrumentSetupOptions) => {
      const code = accountCode.trim();
      if (!code) return;
      const mandatory = options?.mandatory !== false;
      forceOpenSessionRef.current = Boolean(options?.forceOpen);

      const initial = options?.initialSetupData ?? null;
      if (initial) {
        if (
          !accountNeedsInstrumentSetup(initial) &&
          !options?.focusSymbol?.trim() &&
          !options?.forceOpen
        ) {
          // Fully configured — do not flash the wizard.
          return;
        }
      }

      setBrokerAccountCode(code);
      setFocusSymbol(options?.focusSymbol?.trim() || null);
      setMandatory(mandatory);
      setIsOpen(true);
      setLoading(true);
      try {
        // Always POST /scan when the wizard opens (not GET-only, not Trade Review gates).
        const data = await loadInstrumentSetupForDisplay(code);

        setSetupData(data);

        const needsConfirmation = accountNeedsInstrumentSetup(data);

        const keepOpenForSymbolEdit =
          Boolean(options?.focusSymbol?.trim()) || Boolean(options?.forceOpen);

        // Never keep the wizard open when nothing needs confirmation (e.g. 5/5 configured),
        // unless the caller focused a symbol or asked to edit an existing preference.
        if (!needsConfirmation && !keepOpenForSymbolEdit) {
          setIsOpen(false);
          setMandatory(false);
          setFocusSymbol(null);
          await refetch();
        }
      } catch (e) {
        presentMutationError(e, "Failed to load instrument setup.");
        setIsOpen(false);
        setMandatory(false);
        setFocusSymbol(null);
      } finally {
        setLoading(false);
      }
    },
    [refetch]
  );

  const saveRow = useCallback(
    async (
      row: InstrumentSetupRow,
      selection: InstrumentRowEditorSelection,
      consentGranted: boolean,
      resolveContext?: { md_listing_key?: string; instrument_id?: string; asset_class?: string }
    ): Promise<boolean> => {
      const code = brokerAccountCode?.trim();
      if (!code) return false;
      if (!isInstrumentSetupRowReadyToSave({ row, selection, consentGranted })) {
        const blocker = describeInstrumentSetupSaveBlockers({ row, selection, consentGranted });
        toastError(blocker || "Complete all required fields before saving this symbol.");
        return false;
      }

      const tradeSymbol = row.symbol.trim();
      const batchItem = buildInstrumentSetupBatchItem(
        code,
        tradeSymbol,
        selection,
        resolveContext,
        row,
        consentGranted
      );

      setSaving(true);
      try {
        const batchResponse = await setMarketDataPreferencesBatch({
          broker_account_code: code,
          preferences: [batchItem],
        });

        const saved = batchResponse.saved?.find((item) => item.symbol === tradeSymbol);
        if (!batchResponse.ok || batchResponse.saved_count < 1 || !saved?.ok) {
          throw new Error(
            saved && !saved.ok
              ? `Market data preference for ${tradeSymbol} was not saved.`
              : "Market data preference save failed."
          );
        }
        if (consentGranted && batchItem.consent && !saved.consent_at) {
          throw new Error("Market data preference save did not return consent_at.");
        }

        const data = await refreshInstrumentSetupAfterMutation(code);
        setSetupData(data);
        await refetch();
        notifyTradesUpdated({ action: "marketDataPreference", accountCode: code });

        const setupRemaining = accountNeedsInstrumentSetup(data);
        const focusedSymbolSave =
          !mandatory && focusSymbol?.trim() === tradeSymbol;
        if (!setupRemaining || focusedSymbolSave) {
          forceOpenSessionRef.current = false;
          setIsOpen(false);
          setMandatory(false);
          setFocusSymbol(null);
          toast.success(
            setupRemaining
              ? `Saved market data for ${tradeSymbol}`
              : "Instrument setup complete"
          );
        } else {
          toast.success(`Saved market data for ${tradeSymbol}`);
        }
        return true;
      } catch (e) {
        const description =
          e instanceof InstrumentsApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : `Failed to save ${tradeSymbol}.`;
        toastError(`Could not save ${tradeSymbol}`, { description });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [brokerAccountCode, focusSymbol, mandatory, refetch]
  );

  const skipRow = useCallback(
    async (
      row: InstrumentSetupRow,
      reason: MarketDataPreferenceSkipReason = "unsupported"
    ): Promise<boolean> => {
      const code = brokerAccountCode?.trim();
      const tradeSymbol = row.symbol.trim();
      if (!code || !tradeSymbol) return false;

      setSaving(true);
      try {
        await skipMarketDataPreference({
          symbol: tradeSymbol,
          broker_account_code: code,
          reason,
        });
        const data = await refreshInstrumentSetupAfterMutation(code);
        setSetupData(data);
        await refetch();

        const setupRemaining = accountNeedsInstrumentSetup(data);
        if (!setupRemaining) {
          setIsOpen(false);
          setMandatory(false);
          setFocusSymbol(null);
          toast.success("Instrument setup complete");
        } else {
          toast.success(`Skipped ${tradeSymbol} — continuing without charts`);
        }
        return true;
      } catch (e) {
        const description =
          e instanceof InstrumentsApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : `Failed to skip ${tradeSymbol}.`;
        toastError(`Could not skip ${tradeSymbol}`, { description });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [brokerAccountCode, refetch]
  );

  const hydratePendingForActiveAccount = useCallback(
    async (options?: { symbol?: string }): Promise<boolean> => {
      // Never hit GET …/instrument-setup from Trade Review / sims / gates.
      // That endpoint is only for opening the wizard (or Refresh sources).
      // Layer A here uses cached setupData when present, else trade-accounts
      // instrument_setup_pending.
      const cached = setupDataRef.current;
      if (cached) {
        return shouldBlockInstrumentSetupForCharts({
          setupData: cached,
          symbol: options?.symbol,
        });
      }
      return isInstrumentSetupPending(activeAccountPendingRef.current);
    },
    []
  );

  const pendingRows = useMemo(
    () => sortInstrumentSetupPending(setupData?.pending ?? []),
    [setupData?.pending]
  );

  const resolvedRows = useMemo(
    () => setupData?.resolved ?? [],
    [setupData?.resolved]
  );

  const value = useMemo<InstrumentSetupContextValue>(
    () => ({
      isOpen,
      mandatory,
      brokerAccountCode,
      focusSymbol,
      setupData,
      loading,
      saving,
      openSetup,
      closeIfComplete,
      refreshScan,
      dismissWizard,
      saveRow,
      skipRow,
      isBlockingActiveAccount,
      activeAccountPending,
      hydratePendingForActiveAccount,
    }),
    [
      isOpen,
      mandatory,
      brokerAccountCode,
      focusSymbol,
      setupData,
      loading,
      saving,
      openSetup,
      closeIfComplete,
      refreshScan,
      dismissWizard,
      saveRow,
      skipRow,
      isBlockingActiveAccount,
      activeAccountPending,
      hydratePendingForActiveAccount,
    ]
  );

  return (
    <InstrumentSetupContext.Provider value={value}>
      {children}
      <InstrumentSetupWizard
        isOpen={isOpen}
        mandatory={mandatory}
        loading={loading}
        saving={saving}
        setupData={setupData}
        focusSymbol={focusSymbol}
        pendingRows={pendingRows}
        resolvedRows={resolvedRows}
        onSaveRow={saveRow}
        onSkipRow={skipRow}
        onRefreshScan={refreshScan}
        onDismiss={dismissWizard}
      />
    </InstrumentSetupContext.Provider>
  );
}

export function useInstrumentSetup(): InstrumentSetupContextValue {
  const ctx = useContext(InstrumentSetupContext);
  if (!ctx) {
    throw new Error("useInstrumentSetup must be used within InstrumentSetupProvider");
  }
  return ctx;
}

export function useInstrumentSetupOptional(): InstrumentSetupContextValue | null {
  return useContext(InstrumentSetupContext);
}
